import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  connectAuthPost, getConnectToken, getConnectUser, clearConnectSession,
  getConnectDestination, setConnectDestination, normalizePerson, statusLabel
} from './connectApi';
import NotificationsBell from './NotificationsBell';
import ConnectSelect from './ConnectSelect';
import ConnectNav from './ConnectNav';
import './styles/ConnectDiscover.css';
import './styles/ConnectMeet.css';

const STATUS_FILTERS = [
  { id: '', label: 'Everyone' },
  { id: 'local', label: 'Locals' },
  { id: 'traveler', label: 'Travellers' },
  { id: 'returning', label: 'Returning' },
];

/**
 * CONNECT — people in one destination.
 *
 * Every result comes from the server's visibility resolver, so this screen
 * never decides who is visible; it only renders what it is given and
 * explains why a list is empty.
 */
export default function ConnectDiscover() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [destinationId, setDestinationId] = useState(getConnectDestination());
  const [destination, setDestination] = useState(null);

  const [tags, setTags] = useState([]);
  const [people, setPeople] = useState([]);
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState('');
  const [tagId, setTagId] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [search, setSearch] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);

  const interestTags = tags.filter((t) => t.type === 'activity' || t.type === 'style');

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setUser(getConnectUser());

    const boot = async () => {
      try {
        const [destRes, tagRes] = await Promise.all([
          connectAuthPost('connect/destinations/list'),
          connectAuthPost('connect/tags/list'),
        ]);
        if (destRes.success) {
          const list = destRes.data.destinations;
          setDestinations(list);
          const stored = getConnectDestination();
          const me = getConnectUser();
          const chosen =
            (stored && list.some((d) => d.id === stored) && stored) ||
            (me?.destinationId && list.some((d) => d.id === me.destinationId) && me.destinationId) ||
            (list[0] && list[0].id) || null;
          setDestinationId(chosen);
          if (chosen) setConnectDestination(chosen);
        }
        if (tagRes.success) setTags(tagRes.data.tags);
      } catch (err) {
        setNotice('Could not reach the server.');
      }
    };
    boot();
  }, [navigate]);

  const load = useCallback(async () => {
    if (!destinationId) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const payload = { destination_id: destinationId };
      if (status) payload.status = status;
      if (tagId) payload.tag_ids = [parseInt(tagId, 10)];
      if (openOnly) payload.open_to_meet_only = true;
      if (search.trim()) payload.search = search.trim();

      const res = await connectAuthPost('connect/people/discover', payload);
      if (res.success) {
        setPeople(res.data.people.map(normalizePerson));
        setDestination(res.data.destination);
        setReason(res.data.reason || '');
      } else {
        setPeople([]);
        setNotice(res.response || '');
      }
    } catch (err) {
      setNotice('Could not load this destination.');
    } finally {
      setIsLoading(false);
    }
  }, [destinationId, status, tagId, openOnly, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const handleFollow = async (personId) => {
    try {
      const res = await connectAuthPost('connect/users/follow', { user_id: personId });
      if (res.success) {
        setPeople((prev) => prev.map((p) =>
          p.id === personId ? { ...p, isFollowing: res.data.is_following } : p));
      }
    } catch (err) { /* leave the row as it was */ }
  };

  const handleJoinWaitlist = async () => {
    const res = await connectAuthPost('connect/waitlist/join', { destination_id: destinationId });
    setNotice(res.response || '');
  };

  const handleLogout = async () => {
    try { await connectAuthPost('connect/auth/logout'); } catch (err) { /* clear locally regardless */ }
    clearConnectSession();
    navigate('/connect');
  };

  const handleDestinationChange = (id) => {
    const parsed = parseInt(id, 10) || null;
    setDestinationId(parsed);
    setConnectDestination(parsed);
    setStatus(''); setTagId(''); setOpenOnly(false); setSearch(''); setNotice('');
  };

  if (!user) return null;

  const isBuilding = reason === 'building';
  const tooSpecific = reason === 'too_specific';

  return (
    <div className="meet-page">
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="connect" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="connect" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      <main className="meet-main">
        <div className="meet-place">
          <h1 className="meet-place-title">
            <label className="meet-label" htmlFor="connect-destination">People in</label>
            <ConnectSelect
              id="connect-destination"
              variant="title"
              ariaLabel="Destination"
              value={destinationId || ''}
              placeholder={destinations.length === 0 ? 'No destinations open yet' : 'Choose a destination'}
              options={destinations.map((d) => ({ value: d.id, label: d.name }))}
              onChange={handleDestinationChange}
            />
          </h1>
          {destination && (
            <span className={`meet-state meet-state-${destination.state}`}>
              {destination.state === 'building' && 'Building community'}
              {destination.state === 'growing' && `Growing · ${destination.people_range} here`}
              {destination.state === 'active' && `Active · ${destination.people_range} here`}
            </span>
          )}
        </div>

        {notice && <div className="meet-notice">{notice}</div>}

        {!isBuilding && (
          <div className="meet-toolbar">
            <div className="meet-toolbar-row primary">
              <input
                className="meet-search"
                type="search"
                aria-label="Search people by name, bio or location"
                placeholder="Search name, bio or location"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <ConnectSelect
                ariaLabel="Filter by interest"
                value={tagId}
                placeholder="Any interest"
                options={[{ value: '', label: 'Any interest' }]
                  .concat(interestTags.map((t) => ({ value: t.id, label: t.name })))}
                onChange={setTagId}
              />
            </div>

            <div className="meet-toolbar-row">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={status === f.id}
                  className={`meet-chip ${status === f.id ? 'active' : ''}`}
                  onClick={() => setStatus(f.id)}
                >
                  {f.label}
                </button>
              ))}
              <button
                type="button"
                aria-pressed={openOnly}
                className={`meet-chip ${openOnly ? 'active' : ''}`}
                onClick={() => setOpenOnly((v) => !v)}
              >
                Open to meeting
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="meet-empty"><h3>Loading…</h3></div>
        ) : isBuilding ? (
          <div className="meet-empty">
            <h3>Building community in this destination</h3>
            <p>
              We do not show people until there are enough members here for that
              to be safe. Join the waitlist and we will let you know when it opens.
            </p>
            <div className="meet-empty-action">
              <button className="meet-btn primary" onClick={handleJoinWaitlist}>Join the waitlist</button>
            </div>
          </div>
        ) : tooSpecific ? (
          <div className="meet-empty">
            <h3>Too few people match</h3>
            <p>
              Narrow searches are hidden on purpose, so a filter combination cannot
              point at one person. Try removing a filter.
            </p>
          </div>
        ) : people.length === 0 ? (
          <div className="meet-empty">
            <h3>Nobody here yet</h3>
            <p>Try another destination, or check back soon.</p>
          </div>
        ) : (
          <div className="meet-grid">
            {people.map((p) => (
              <article key={p.id} className="person-card">
                <div className="person-card-head">
                  <div className="meet-person-avatar">
                    {p.profilePicture
                      ? <img src={p.profilePicture} alt="" />
                      : <span aria-hidden="true">{p.fullName.charAt(0).toUpperCase()}</span>}
                  </div>
                  <div className="person-card-id">
                    <span className="person-card-name">{p.fullName}</span>
                    <div className="person-card-meta">
                      {p.pronouns && <span className="meet-person-pronouns">{p.pronouns}</span>}
                      <span className="meet-person-status">
                        {statusLabel(p.status)}{p.isLocalGuide ? ' · Local guide' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Presence is one line, not a stack of paragraphs */}
                {(p.availability || p.trip) && (
                  <p className="person-card-presence">
                    {p.availability && (
                      <span className="live">Free for {p.availability.minutes_left} more minutes</span>
                    )}
                    {p.availability && p.trip && ' · '}
                    {p.trip && p.trip.until_label}
                  </p>
                )}

                {p.bio && <p className="person-card-bio">{p.bio}</p>}

                {p.tags.length > 0 && (
                  <div className="person-card-tags">
                    {p.tags.slice(0, 3).map((t) => (
                      <span key={t.id} className="person-card-tag">{t.name}</span>
                    ))}
                    {p.tags.length > 3 && (
                      <span className="person-card-tag">+{p.tags.length - 3}</span>
                    )}
                  </div>
                )}

                {p.reason && <p className="person-card-reason">{p.reason}</p>}

                <div className="person-card-actions">
                  <button
                    type="button"
                    aria-pressed={p.isFollowing}
                    className={`meet-btn ${p.isFollowing ? 'ghost' : 'primary'} small`}
                    onClick={() => handleFollow(p.id)}
                  >
                    {p.isFollowing ? 'Following' : 'Follow'}
                  </button>
                  <Link to={`/connect/user/${p.id}`} className="meet-btn ghost small">
                    View profile
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
